import React from "react";
import CountUp from "@/components/CountUp";
import { formatMoney, formatNumber } from "@/lib/format";

/**
 * Unified KPI card used by every module's dashboard.
 *
 * <KpiCard
 *   label="Cash on Hand"
 *   value={12480}
 *   money
 *   tone={cash > 0 ? "positive" : "attention"}
 *   sub="AR $6,888"
 *   icon={Coins}
 * />
 *
 * Tones:
 *   • neutral   — navy hero (headline metric, no judgement)
 *   • positive  — emerald tint  (good outcome)
 *   • attention — amber tint    (needs a look)
 *   • critical  — red tint      (act now)
 */
const TONE = {
  neutral: {
    wrap: "bg-[#0A2540] text-white border-transparent",
    eyebrow: "text-[#C9A356]",
    sub: "text-white/70",
    icon: "text-[#C9A356] bg-white/10",
  },
  positive: {
    wrap: "bg-white dark:bg-slate-900 border-[#E2E8F0] dark:border-slate-700 text-[#0A2540] dark:text-white",
    eyebrow: "text-emerald-600",
    sub: "text-[#475569] dark:text-slate-400",
    icon: "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20",
  },
  attention: {
    wrap: "bg-white dark:bg-slate-900 border-amber-300/70 dark:border-amber-500/40 text-[#0A2540] dark:text-white",
    eyebrow: "text-amber-600",
    sub: "text-[#475569] dark:text-slate-400",
    icon: "text-amber-600 bg-amber-50 dark:bg-amber-900/20",
  },
  critical: {
    wrap: "bg-white dark:bg-slate-900 border-red-300 dark:border-red-500/40 text-[#0A2540] dark:text-white",
    eyebrow: "text-red-600",
    sub: "text-[#475569] dark:text-slate-400",
    icon: "text-red-600 bg-red-50 dark:bg-red-900/20",
  },
};

export default function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  tone = "neutral",
  money = false,
  decimals = 0,
  prefix = "",
  suffix = "",
  testId,
  href,   // optional — makes the whole card a link
  onClick,
}) {
  const t = TONE[tone] || TONE.neutral;
  const numeric = Number(value);
  const formattedValue = money ? formatMoney(numeric, { decimals }) : `${prefix}${formatNumber(numeric, decimals)}${suffix}`;
  const showAnim = Number.isFinite(numeric);

  const inner = (
    <div
      data-testid={testId}
      className={`rounded-2xl border p-5 sm:p-6 transition-all duration-200 ${t.wrap} ${
        href || onClick
          ? "hover:-translate-y-0.5 hover:shadow-md cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A356]"
          : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className={`text-[10px] uppercase tracking-[0.14em] font-bold ${t.eyebrow}`}>
            {label}
          </p>
          <div className="mt-1.5 font-heading text-3xl sm:text-[34px] leading-none">
            {showAnim ? (
              money ? (
                <CountUp value={numeric} prefix="$" decimals={decimals} />
              ) : (
                <CountUp value={numeric} prefix={prefix} suffix={suffix} decimals={decimals} />
              )
            ) : (
              <span className="tabular-nums">{formattedValue}</span>
            )}
          </div>
          {sub ? <p className={`text-xs mt-2 ${t.sub}`}>{sub}</p> : null}
        </div>
        {Icon ? (
          <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${t.icon}`}>
            <Icon className="w-5 h-5" />
          </div>
        ) : null}
      </div>
    </div>
  );

  if (href) {
    // Lazy-avoid react-router import — keep this component framework-neutral.
    return (
      <a href={href} className="block focus:outline-none">
        {inner}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="block w-full text-left focus:outline-none">
        {inner}
      </button>
    );
  }
  return inner;
}

/** Grid wrapper — pass N cards, get a responsive KPI strip. */
export function KpiGrid({ children, className = "" }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 ${className}`}>
      {children}
    </div>
  );
}
